import { nullable2undefinable } from 'core/utils/commonUtils';
import { http } from 'core/utils/httpClient';
import type { CreateOrUpdateOrderProductRequest } from './dtoModels/order/createOrUpdateOrderProductRequest';
import type { CreateOrUpdateOrderProjectRequest } from './dtoModels/order/createOrUpdateOrderProjectRequest';
import type { UpdateOrderProductPositionRequest } from './dtoModels/order/updateOrderProductPositionRequest';
import type {
  OrderProjectResponse,
  ProjectProductMarkupResponse,
  ProjectProductResponse,
} from './dtoModels/order/orderProjectResponse';
import type {
  OrderProject,
  ProjectProduct,
  ProjectProductMarkup,
} from 'models/order/orderProject';

export const mapOrderProject = (source: OrderProjectResponse): OrderProject => ({
  ...source,
  user: nullable2undefinable(source.user),
  dateCreate: new Date(source.dateCreate),
  orderProducts: mapOrderProducts(source.orderProducts),
});

export const mapOrderProjects = (source: OrderProjectResponse[]): OrderProject[] =>
  source.map((s) => mapOrderProject(s));

export const mapOrderProduct = (source: ProjectProductResponse): ProjectProduct => ({
  ...source,
  productId: nullable2undefinable(source.productId),
  markups: mapMarkups(source.markups),
});

export const mapMarkups = (
  markups: ProjectProductMarkupResponse[]
): ProjectProductMarkup[] =>
  markups.map((m) => ({
    ...m,
    user: nullable2undefinable(m.user),
    dateCreate: new Date(m.dateCreate),
  }));

export const mapOrderProducts = (source: ProjectProductResponse[]): ProjectProduct[] =>
  source.map((s) => mapOrderProduct(s));

export async function getOrderProjectsAsync(orderId: number): Promise<OrderProject[]> {
  const { data } = await http.get<OrderProjectResponse[]>(`orders/${orderId}/projects`);

  return mapOrderProjects(data);
}

export async function createOrderProjectAsync(
  orderId: number,
  request: CreateOrUpdateOrderProjectRequest
): Promise<OrderProject> {
  const { data } = await http.post<OrderProjectResponse>(
    `orders/${orderId}/projects`,
    request
  );

  return mapOrderProject(data);
}

export async function copyOrderProjectAsync(
  orderId: number,
  projectId: number,
  request: CreateOrUpdateOrderProjectRequest
): Promise<OrderProject> {
  const { data } = await http.post<OrderProjectResponse>(
    `orders/${orderId}/projects/${projectId}/copy`,
    request
  );

  return mapOrderProject(data);
}

export async function updateOrderProjectAsync(
  orderId: number,
  projectId: number,
  request: CreateOrUpdateOrderProjectRequest
): Promise<OrderProject> {
  const { data } = await http.put<OrderProjectResponse>(
    `orders/${orderId}/projects/${projectId}`,
    request
  );

  return mapOrderProject(data);
}

export async function createProductAsync(
  orderId: number,
  projectId: number,
  request: CreateOrUpdateOrderProductRequest
): Promise<ProjectProduct> {
  const { data } = await http.post<ProjectProductResponse>(
    `orders/${orderId}/projects/${projectId}/products`,
    request
  );

  return mapOrderProduct(data);
}

export async function updateProductAsync(
  orderId: number,
  projectId: number,
  productId: number,
  request: CreateOrUpdateOrderProductRequest
): Promise<ProjectProduct> {
  const { data } = await http.put<ProjectProductResponse>(
    `orders/${orderId}/projects/${projectId}/products/${productId}`,
    request
  );

  return mapOrderProduct(data);
}

export async function deleteProductAsync(
  orderId: number,
  projectId: number,
  productId: number
): Promise<void> {
  await http.delete(`orders/${orderId}/projects/${projectId}/products/${productId}`);
}

export async function updateProductPositionsAsync(
  orderId: number,
  projectId: number,
  positions: UpdateOrderProductPositionRequest[]
): Promise<void> {
  await http.put(`orders/${orderId}/projects/${projectId}/products/positions`, positions);
}
